'use client'

import { cx } from '@/lib/utils'

import { GeneralPopupActions } from './general-popup-actions'
import { GeneralPopupHeading } from './general-popup-heading'
import { GeneralPopupImage } from './general-popup-image'
import { GeneralPopupList } from './general-popup-list'
import { GeneralPopupPhoneLink } from './general-popup-phone-link'
import type { PopupTemplateProps } from './template-props'

export const GeneralPopup = ({ popup, onCtaClick, reason }: PopupTemplateProps) => {
  const isExitIntent = reason === 'desktop-mouseleave-top'
  const eyebrow = isExitIntent ? (popup.exitEyebrow ?? popup.eyebrow) : popup.eyebrow
  const body = isExitIntent ? (popup.exitBody ?? popup.body) : popup.body
  const hasImage = Boolean(popup.imageUrl)

  return (
    <div
      data-popup-layout
      className={cx(
        'flex h-full max-h-[inherit] flex-col overflow-hidden pt-[max(2.5rem,calc(env(safe-area-inset-top)+0.75rem))] pr-[max(1.5rem,env(safe-area-inset-right))] pb-[max(1.5rem,env(safe-area-inset-bottom))] pl-[max(1.5rem,env(safe-area-inset-left))] md:h-auto md:max-h-none md:overflow-visible md:px-10 md:pt-12 md:pb-10',
        hasImage ? 'md:grid md:grid-cols-12 md:gap-x-8' : 'md:block'
      )}
    >
      <div
        data-popup-scroll-region
        className={cx(
          'modal-scrollbar grid min-h-0 grow basis-auto grid-cols-1 gap-4 overflow-y-auto overscroll-contain',
          hasImage ? 'md:contents' : 'md:block md:overflow-visible'
        )}
      >
        {hasImage ? <GeneralPopupHeading eyebrow={eyebrow} title={popup.title} className="md:hidden" /> : null}

        {hasImage ? (
          <div className="flex flex-col gap-4 md:col-span-5 md:row-span-2 md:row-start-1">
            <GeneralPopupImage popup={popup} />
            <GeneralPopupPhoneLink
              popup={popup}
              reason={reason}
              onCtaClick={onCtaClick}
              variant="inline"
              className="hidden md:flex"
            />
          </div>
        ) : null}

        <div className={cx('min-w-0', hasImage && 'md:col-span-7 md:col-start-6 md:row-start-1')}>
          <div className="md:space-y-4">
            <GeneralPopupHeading eyebrow={eyebrow} title={popup.title} className={hasImage ? 'hidden md:block' : ''} />
            {body ? <p className="text-navy text-[16px] leading-relaxed tracking-tight md:text-md">{body}</p> : null}
            <GeneralPopupList title={popup.listTitle} items={popup.listItems} />
          </div>
        </div>
      </div>
      <div
        data-popup-footer
        className={cx('mt-6 flow-root shrink-0', hasImage && 'md:col-span-7 md:col-start-6 md:row-start-2 md:self-end')}
      >
        <GeneralPopupActions popup={popup} reason={reason} hasImage={hasImage} onCtaClick={onCtaClick} />
      </div>
    </div>
  )
}
